/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { FC, useEffect, useMemo, useState } from "react";
import { AppLoader } from "@advapacs/common";
import { LoginForm } from "./LoginForm";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "@src/config/store";
import { getConfiguration } from "./ducks/operations";
import { LinkNotFound } from "@src/components/LinkNotFound";
import { useGetHeroUrl } from "@src/hooks/useGetHeroUrl";
import { useDetectSmallScreen } from "@src/hooks/useDetectSmallScreen";
import { useSelector } from "react-redux";
import { IRootState } from "@src/config/reducers";

const LoginContainer: FC = () => {
  const dispatch = useAppDispatch();
  const { token } = useParams();
  const [linkError, setLinkError] = useState(false);
  const heroUrl = useGetHeroUrl();
  const {
    isAuthenticated,
    sessionHasBeenFetched,
    linkError: configurationError,
  } = useSelector((state: IRootState) => state.unAuthenticatedReducer);
  const isSmallScreen = useDetectSmallScreen();

  useEffect(() => {
    if (!token) setLinkError(true);

    if (token) {
      const decoded = atob(token);
      const [region, accessLinkId] = decoded?.split(".");

      if (!region || !accessLinkId) setLinkError(true);
    }
  }, [token]);

  useEffect(() => {
    if (token && !linkError) dispatch(getConfiguration(token));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const hasBanner = useMemo(() => !!heroUrl, [heroUrl]);

  if (linkError) {
    return <LinkNotFound showFooter={false} />;
  }

  if (isAuthenticated && sessionHasBeenFetched) {
    window.location.href = "/Exams"; // redirect to exams.
    return;
  }

  // The access link no longer exists, e.g. it was removed with its patient.
  if (String(configurationError?.code) === "404") {
    return (
      <LinkNotFound
        showFooter={false}
        message="This link is no longer valid because the associated information has been removed."
        showHomeButton
      />
    );
  }

  if (configurationError) {
    return <LinkNotFound showFooter={false} />;
  }

  return (
    <>
      <div
        className={`d-flex ${
          hasBanner
            ? "has-banner justify-content-start"
            : "justify-content-center"
        } login-full-height ${isSmallScreen ? "small-screen" : ""}`}
      >
        {hasBanner && (
          <AppLoader.ImageLoader
            className="d-mobile-none d-tablet-none"
            url={heroUrl}
            width={"45%"}
          />
        )}

        <LoginForm
          className={`${
            hasBanner
              ? "d-flex justify-content-center flex-1 portal-banner"
              : ""
          }`}
        />
      </div>
    </>
  );
};

export default LoginContainer;
